import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { get, size, drop } from 'lodash';
import { TooltipHost } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { format } from 'date-fns';
import Link from '../Link';
import Callout from '../../components/Callout';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import ModuleContentList from '../../components/ModuleContentList';
import SearchBar from '../../components/SearchBar';
import TextButton from '../../components/TextButton';
import ProductsList from '../../components/ProductsList';
import { fetchAssociatedProducts } from '../../actions/contracts';
import saveExcel from '../../utils/excel';
import styles from './PartnerContractProductList.style';

const PartnerContractProductList = ({ contractName, productsAssociated }) => {
  const dispatch = useDispatch();
  const { contractAddress } = useParams();
  const [loading, setLoading] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const associatedProducts = useSelector(state =>
    Object.values(get(state, `entities.associatedProducts`, {})).filter(
      product => product.address === contractAddress,
    ),
  ).map(product => ({
    ...product,
    productIds: product.productIds?.map(id => {
      return id.split(':') && id.split(':').length ? id.split(':')[0] : id;
    }),
  }));

  const productCount = showSearchResults ? searchResults?.length : associatedProducts?.length;

  useEffect(() => {
    async function loadProducts() {
      if (associatedProducts.length === 0) {
        await dispatch(fetchAssociatedProducts(contractAddress, productsAssociated));
      }
      setLoading(false);
    }
    if (productsAssociated) loadProducts();
  }, [dispatch]);

  // returns items for cell in excel
  const itemsForCell = items => {
    const itemsArray = [];
    items.forEach(item => {
      const itemObj = {};
      itemObj.name = item.name;
      itemObj.productIds = item.productIds?.join(', ') || '';
      itemObj.id = item.id ? parseFloat(item.id) : '';
      itemsArray.push(itemObj);
    });
    return itemsArray;
  };

  const exportToExcel = () => {
    const productItemsHeader = ['Product Title', 'Product ID', 'Token ID'];
    const sheetHeaders = [
      {
        cell: 'A1',
        value: 'Contract Name:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B1',
        value: contractName,
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
    ];
    const columnsHeader = productItemsHeader;
    saveExcel(
      columnsHeader,
      itemsForCell(showSearchResults ? searchResults : associatedProducts),
      sheetHeaders,
      {
        fileName: `${contractName} - ${format(new Date(), 'MM/dd/yyyy')}`,
        workSheetName: 'Associated Products',
      },
    );
  };

  const handleClear = () => {
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const handleSearch = async query => {
    if (!query) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    setLoading(true);
    setShowSearchResults(true);
    const results = await associatedProducts.filter(
      product =>
        product.productIds.some(ids => ids.toLowerCase().includes(query.toLowerCase())) ||
        product.name.toLowerCase().includes(query.toLowerCase()),
    );
    setSearchResults(results);
    setLoading(false);
  };

  const columns = [
    {
      key: 'icon',
      name: 'icon',
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="Tag" ariaLabel="Tag" className={styles.detailRowIconColor} />
          </div>
        );
      },
    },
    {
      key: 'name',
      name: 'Product Title',
      fieldName: 'name',
      minWidth: 220,
      maxWidth: 350,
      isResizable: true,
    },
    {
      key: 'productId',
      name: 'Product ID',
      fieldName: 'productId',
      isMultiline: true,
      minWidth: 100,
      maxWidth: 280,
      isResizable: true,
      onRender: item => {
        const otherProductIds = drop(get(item, 'productIds'));
        const productIdsToolTip = otherProductIds.map(product => (
          <span key={product}>
            {product} <br />
          </span>
        ));

        const moreProductIds = size(get(item, 'productIds')) - 1;
        return (
          <div className={styles.productText}>
            {get(item, 'productIds[0]')}
            {moreProductIds > 0 && (
              <TooltipHost className={styles.tooltipContent} content={productIdsToolTip}>
                <span className={styles.moreProductIds}>
                  {' '}
                  <br /> +{moreProductIds} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    {
      key: 'tokenId',
      name: 'Token ID',
      fieldName: 'tokenId',
      minWidth: 150,
      maxWidth: 190,
      isResizable: true,
      onRenderHeader: () => {
        return (
          <div className={styles.tokenHeader}>
            <span>Token ID</span>
            <Callout content="Products are digitally represented as tokens. This ID is the unique identifier for each product token." />
          </div>
        );
      },
      onRender: ({ id }) => {
        return `#${id}`;
      },
    },
    {
      key: 'tasks',
      isResizable: false,
      minWidth: 70,
      maxWidth: 70,
      onRender: item => {
        return (
          <Link className={styles.viewButton} href={`/products/${item.id}`} target="_blank">
            View
          </Link>
        );
      },
    },
  ];

  return (
    <Module data-testid="associated-contract-products">
      <ModuleHeader>Associated Products</ModuleHeader>
      <div className={styles.searchContainer}>
        <SearchBar
          placeholder="Search Product Title / Product ID"
          ariaLabel="Search"
          underlined
          onChange={handleSearch} // when you type
          onClear={handleClear} // when you click X
          data-testid="search-input"
          className={styles.search}
        />
        {productCount > 0 && (
          <TextButton className={styles.exportToExcel} onClick={exportToExcel}>
            <FontIcon
              iconName="DownloadDocument"
              arianame="DownloadDocument"
              className={styles.bulletedList}
            />
            Export to Excel
          </TextButton>
        )}
      </div>
      <ModuleContentList>
        {(productCount !== 0 || loading) && (
          <ProductsList
            columns={columns}
            items={showSearchResults ? searchResults : associatedProducts}
            shimmered
            className={styles.scrollablePane}
            enableShimmer={loading}
            disableRowLink
            multiLine
          />
        )}
      </ModuleContentList>
      {!loading && (
        <ModuleHeader className={styles.productsCount}>{`${productCount} Products`}</ModuleHeader>
      )}
    </Module>
  );
};

PartnerContractProductList.propTypes = {
  contractName: PropTypes.string,
  productsAssociated: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      propertyType: PropTypes.string.isRequired,
      property: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
};

PartnerContractProductList.defaultProps = {
  contractName: '',
  productsAssociated: [],
};

export default PartnerContractProductList;
